import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { FaAtom, FaCode, FaMagic } from "react-icons/fa";

function TrefoilHomeScene() {
  const mountRef = useRef(null);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    const width = container.clientWidth || 600;
    const height = container.clientHeight || 580;

    // 1. Scene, Camera, Renderer
    const scene = new THREE.Scene();
    scene.position.y = 0.1;
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 0, 8.5);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    // 2. Build 3D Trefoil Knot Particles Geometry
    const COUNT = 6500;
    const geometry = new THREE.BufferGeometry();

    const indices = new Float32Array(COUNT);
    const scales = new Float32Array(COUNT);
    const colors = new Float32Array(COUNT * 3);
    const positions = new Float32Array(COUNT * 3);

    // Palette: Cyan (#38bdf8), Violet (#8b5cf6), Magenta (#ec4899), Gold (#f59e0b)
    const colorPalette = [
      new THREE.Color("#38bdf8"),
      new THREE.Color("#8b5cf6"),
      new THREE.Color("#ec4899"),
      new THREE.Color("#f59e0b"),
      new THREE.Color("#06b6d4"),
    ];

    for (let i = 0; i < COUNT; i++) {
      indices[i] = i;
      scales[i] = 0.75 + Math.random() * 0.95;

      // Color gradient along curve index with smooth blending
      const tRatio = i / COUNT;
      const colorIndex = (tRatio * (colorPalette.length - 1)) % (colorPalette.length - 1);
      const idx1 = Math.floor(colorIndex);
      const idx2 = idx1 + 1;
      const factor = colorIndex - idx1;

      const c = new THREE.Color();
      c.lerpColors(colorPalette[idx1], colorPalette[idx2], factor);
      c.multiplyScalar(0.85 + Math.random() * 0.4);

      colors[i * 3 + 0] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;

      positions[i * 3 + 0] = 0;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = 0;
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("aIndex", new THREE.BufferAttribute(indices, 1));
    geometry.setAttribute("aScale", new THREE.BufferAttribute(scales, 1));
    geometry.setAttribute("aColor", new THREE.BufferAttribute(colors, 3));

    // Custom Shader Material for GLSL Trefoil Knot Particle System
    const uniforms = {
      uTime: { value: 0 },
      uCount: { value: COUNT },
      uTubeRadius: { value: 0.34 },
      uScale: { value: 0.72 },
      uMouse3D: { value: new THREE.Vector3(999, 999, 999) },
      uMouseHover: { value: 0 },
    };

    const vertexShader = `
      attribute float aIndex;
      attribute float aScale;
      attribute vec3 aColor;

      uniform float uTime;
      uniform float uCount;
      uniform float uTubeRadius;
      uniform float uScale;
      uniform vec3 uMouse3D;
      uniform float uMouseHover;

      varying vec3 vColor;
      varying float vDistToMouse;

      const float TWO_PI = 6.28318530718;
      const float GOLDEN_ANGLE = 2.39996322973;

      vec3 getTrefoilCurve(float t) {
        return vec3(
          sin(t) + 2.0 * sin(2.0 * t),
          cos(t) - 2.0 * cos(2.0 * t),
          -sin(3.0 * t)
        );
      }

      void main() {
        vColor = aColor;
        float i = aIndex;
        // Continuous particle flow along the 3D Trefoil Knot loop
        float t = mod((i / uCount) * TWO_PI + uTime * 0.35, TWO_PI);

        // Tangent via finite difference (Frenet-Serret Frame)
        float eps = 0.002;
        vec3 pPrev = getTrefoilCurve(t - eps);
        vec3 pNext = getTrefoilCurve(t + eps);
        vec3 T = normalize(pNext - pPrev);
        vec3 up = abs(T.y) > 0.95 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0);
        vec3 B = normalize(cross(T, up));
        vec3 N = normalize(cross(T, B));

        // Radial offset around the tube with dynamic spiral spin
        float phi = GOLDEN_ANGLE * i * 7.3 + uTime * 0.6;
        vec3 radial = N * cos(phi) + B * sin(phi);

        // Pulsation wave along the tube
        float wave = sin(uTime * 2.5 + t * 6.0) * 0.15 + 1.0;
        float radius = uTubeRadius * aScale * wave;

        // Base 3D Trefoil position
        vec3 pos = (getTrefoilCurve(t) + radial * radius) * uScale;

        // Calculate 3D distance to cursor
        float dist3D = distance(pos, uMouse3D);
        vDistToMouse = dist3D;

        // Interactive mouse displacement radius in 3D world space
        float effectRadius = 2.5;
        if (dist3D < effectRadius) {
          float factor = (1.0 - dist3D / effectRadius) * uMouseHover;
          vec3 pushDir = normalize(pos - uMouse3D + vec3(0.0001));
          pos += pushDir * (factor * 0.45) + radial * (factor * 0.3);
        }

        vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
        gl_Position = projectionMatrix * mvPosition;

        // Size attenuation by perspective depth & subtle mouse proximity enlargement
        float hoverSize = 1.0 + (1.0 - smoothstep(0.0, effectRadius, dist3D)) * 0.35 * uMouseHover;
        gl_PointSize = (35.0 * aScale / -mvPosition.z) * hoverSize;
      }
    `;

    const fragmentShader = `
      varying vec3 vColor;
      varying float vDistToMouse;

      void main() {
        vec2 coord = gl_PointCoord - vec2(0.5);
        float dist = length(coord);
        if (dist > 0.5) discard;

        float alpha = smoothstep(0.5, 0.0, dist);
        alpha = pow(alpha, 1.1);

        // High intensity mouse glow boost
        float mouseGlow = (1.0 - smoothstep(0.0, 2.5, vDistToMouse));
        vec3 finalColor = mix(vColor, vec3(1.0, 0.95, 0.85), mouseGlow * 0.45);
        finalColor *= (1.0 + mouseGlow * 1.6);

        gl_FragColor = vec4(finalColor, alpha * 0.95);
      }
    `;

    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const trefoilMesh = new THREE.Points(geometry, material);
    scene.add(trefoilMesh);

    // 3. Ambient Dust Particles Layer
    const DUST_COUNT = 300;
    const dustGeo = new THREE.BufferGeometry();
    const dustPos = new Float32Array(DUST_COUNT * 3);
    for (let i = 0; i < DUST_COUNT * 3; i += 3) {
      dustPos[i] = (Math.random() - 0.5) * 12;
      dustPos[i + 1] = (Math.random() - 0.5) * 12;
      dustPos[i + 2] = (Math.random() - 0.5) * 10;
    }
    dustGeo.setAttribute("position", new THREE.BufferAttribute(dustPos, 3));
    const dustMat = new THREE.PointsMaterial({
      color: 0x38bdf8,
      size: 0.04,
      transparent: true,
      opacity: 0.35,
      blending: THREE.AdditiveBlending,
    });
    const dustMesh = new THREE.Points(dustGeo, dustMat);
    scene.add(dustMesh);

    // 4. Global 3D Raycasting Mouse & Touch Interaction
    const raycaster = new THREE.Raycaster();
    const mouseVec = new THREE.Vector2();
    const targetPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    const intersectPoint = new THREE.Vector3();

    let targetRotX = 0;
    let targetRotY = 0;
    let isHovered = false;

    const updatePointer = (clientX, clientY) => {
      const rect = container.getBoundingClientRect();
      // Check if pointer is within or near container
      const insideX = clientX >= rect.left - 100 && clientX <= rect.right + 100;
      const insideY = clientY >= rect.top - 100 && clientY <= rect.bottom + 100;

      if (!insideX || !insideY) {
        isHovered = false;
        uniforms.uMouse3D.value.set(999, 999, 999);
        return;
      }

      isHovered = true;
      mouseVec.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      mouseVec.y = -((clientY - rect.top) / rect.height) * 2 + 1;

      // Raycast 3D intersection on z = 0 plane
      raycaster.setFromCamera(mouseVec, camera);
      raycaster.ray.intersectPlane(targetPlane, intersectPoint);
      if (intersectPoint) {
        uniforms.uMouse3D.value.copy(intersectPoint);
      }

      targetRotY = mouseVec.x * 0.85;
      targetRotX = -mouseVec.y * 0.55;
    };

    const handlePointerMove = (e) => {
      updatePointer(e.clientX, e.clientY);
    };

    const handleTouchMove = (e) => {
      if (e.touches && e.touches[0]) {
        updatePointer(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const handlePointerLeave = () => {
      isHovered = false;
      uniforms.uMouse3D.value.set(999, 999, 999);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("pointerleave", handlePointerLeave);

    // 5. Animation Loop
    let animationFrameId;
    const clock = new THREE.Clock();

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      const elapsedTime = clock.getElapsedTime();

      uniforms.uTime.value = elapsedTime;
      const targetHover = isHovered ? 1.0 : 0.0;
      uniforms.uMouseHover.value += (targetHover - uniforms.uMouseHover.value) * 0.08;

      // Floating 3D bobbing motion & floating position
      scene.position.y = 0.1 + Math.sin(elapsedTime * 1.2) * 0.15;
      scene.position.x = Math.cos(elapsedTime * 0.8) * 0.08;

      // Continuous 3D rotation + smooth mouse tilt inertia
      trefoilMesh.rotation.y += 0.007;
      trefoilMesh.rotation.x = Math.sin(elapsedTime * 0.4) * 0.2 + (targetRotX * 0.35);
      trefoilMesh.rotation.z = Math.cos(elapsedTime * 0.5) * 0.15;

      trefoilMesh.rotation.y += (targetRotY - (trefoilMesh.rotation.y % (Math.PI * 2))) * 0.05;

      dustMesh.rotation.y = elapsedTime * 0.03;

      renderer.render(scene, camera);
    };

    animate();

    // 6. Handle Window Resize
    const handleResize = () => {
      if (!container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("pointerleave", handlePointerLeave);
      window.removeEventListener("resize", handleResize);

      if (container && renderer.domElement) {
        container.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      dustGeo.dispose();
      dustMat.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <div
      className="artistic-hero-container trefoil-particles-hero"
      style={{
        position: "relative",
        width: "100%",
        height: "600px",
        overflow: "visible",
      }}
    >
      {/* 3D GLSL Trefoil Particle Knot WebGL Canvas */}
      <div ref={mountRef} className="three-webgl-canvas" style={{ width: "100%", height: "100%" }} />

      {/* Floating Interactive Glassmorphism Badges (pointer-events: none so mouse reaches canvas) */}
      <div className="hero-animated-objects" style={{ pointerEvents: "none" }}>
        <div className="hero-obj obj-code-bracket" title="3D Trefoil Knot Particles">
          <div className="obj-clay-orb orb-purple">
            <FaMagic className="code-symbol-icon" />
          </div>
        </div>

        <div className="hero-obj obj-react-atom" title="React 3D">
          <div className="obj-clay-orb orb-blue">
            <FaAtom className="atom-icon" />
          </div>
        </div>

        <div className="hero-obj obj-gear" title="GLSL Shader">
          <div className="obj-clay-orb orb-pink">
            <FaCode className="gear-icon" />
          </div>
        </div>

        <div className="hero-obj obj-clay-bubble bubble-1"></div>
        <div className="hero-obj obj-clay-bubble bubble-2"></div>
      </div>

      {/* Glowing Backdrop Aura (pointer-events: none) */}
      <div className="artistic-glow-aura" style={{ pointerEvents: "none" }}></div>
    </div>
  );
}

export default TrefoilHomeScene;
